import { useState, useEffect } from "react";
import {
    Upload,
    Eye,
    CheckCircle,
    Clock,
    ArrowUpRight,
    ArrowDownLeft,
    Search,
    Filter,
    MoreVertical,
    X,
    AlertTriangle,
    DollarSign,
    Activity,
    TrendingUp,
    Users,
    Wallet,
    Building2,
    Phone,
    User,
    RefreshCw,
    Star,
    Shield,
    Zap
} from "lucide-react";
import WrapUpP2P from "./layouts/WrapUpP2p";
import { useDispatch, useSelector } from "react-redux";
import { allRemoteSettingsSelect, exchangeSelect, peerSelect, userSelect } from "../../../redux/selectors";
import { useNavigate } from "react-router-dom";
import { formatDate, formatTime, guessAdminRoute, money } from "../../../utility/Helper1";
import { peerActions } from "../../../redux/actions";
import notify from "../../../utility/NotificationSystem";
import Countdown from "react-countdown";

const P2POrderDisplay = () => {
    const [activeFilter, setActiveFilter] = useState("all");
    const [directionFilter, setDirectionFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showProofModal, setShowProofModal] = useState(false);
    const [selectedProof, setSelectedProof] = useState("");
    const [loadingActions, setLoadingActions] = useState({});

    // Current user ID
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userData = useSelector(userSelect);

    const site_settings = useSelector(allRemoteSettingsSelect);
    const settings = site_settings?.operational_settings;
    const peerRoot = useSelector(peerSelect);
    const exchangeRoot = useSelector(exchangeSelect);
    const orders = peerRoot?.filter(el => !['BLOCKED', 'PAID'].includes(el.status));
    const currentUserAccountId = userData?.accountId;

    const formatTimeAgo = (timestamp) => {
        const diff = Date.now() - timestamp;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        return 'Just now';
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 'PAID':
                return {
                    label: 'Completed',
                    color: 'bg-emerald-900/30 text-emerald-300 border-emerald-600/30',
                    icon: CheckCircle,
                    glow: 'shadow-emerald-500/20'
                };
            case 'YES':
                return {
                    label: 'Pending Confirmation',
                    color: 'bg-amber-900/30 text-amber-300 border-amber-600/30',
                    icon: Clock,
                    glow: 'shadow-amber-500/20'
                };
            case 'NO':
                return {
                    label: 'Awaiting Proof',
                    color: 'bg-orange-900/30 text-orange-300 border-orange-600/30',
                    icon: Upload,
                    glow: 'shadow-orange-500/20'
                };
            default:
                return {
                    label: 'Unknown',
                    color: 'bg-gray-800/30 text-gray-400 border-gray-600/30',
                    icon: AlertTriangle,
                    glow: 'shadow-gray-500/20'
                };
        }
    };


    const handleFileUpload = (orderId) => {
        document.getElementById(`file-${orderId}`).click();
    };


    const handleFileChange = (e, orderId) => {

        // setUploadingProof(true)

        if (e.target.files && e.target.files[0]) {
            // // In a real app, you would handle the file upload here
            // console.log(`File selected for order ${orderId}:`, e.target.files[0].name);

            const file = e.target.files[0];
            dispatch(peerActions.onPost({ image: file }, `p2p/payment/upload/${orderId}`, response => {
                // setUploadingProof(false)
                notify(response.msg, response.code)
            }),
            )

        }
    };



    const reportTransaction = (orderId) => {

        // setReportingProof(true)

        dispatch(peerActions.onGet(`p2p/payment/report/${orderId}`, response => {
            // setReportingProof(false)
            notify(response.msg, response.code)
        }),
        )

    };



    // Handle marking as paid
    const handleMarkAsPaid = (orderId) => {
        // setConfirmingProof(true)

        dispatch(peerActions.onPost({ paid: 1 }, `p2p/payment/confirm/${orderId}`, response => {
            // setConfirmingProof(false)
            notify(response.msg, response.code)
            setShowProofModal(false)
        }),
        )
    };


    // Handle actions
    const handleAction = async (action, orderId) => {
        setLoadingActions(prev => ({ ...prev, [`${action}-${orderId}`]: true }));

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        setLoadingActions(prev => ({ ...prev, [`${action}-${orderId}`]: false }));
    };

    // Filter orders
    const myOrders = orders.filter(order =>
        order.pherAccountId == currentUserAccountId || order.gherAccountId == currentUserAccountId
    );

    const filteredOrders = myOrders.filter(order => {
        // Search filter
        const matchesSearch = order.transid.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.pherUsername.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.gherUsername.toLowerCase().includes(searchQuery.toLowerCase());

        // Direction filter
        const isOutgoing = order.pherAccountId == currentUserAccountId;
        const isIncoming = order.gherAccountId == currentUserAccountId;

        let matchesDirection = true;
        if (directionFilter == "outgoing") matchesDirection = isOutgoing;
        else if (directionFilter == "incoming") matchesDirection = isIncoming;

        // Status filter
        let matchesStatus = true;
        if (activeFilter == "pending") matchesStatus = order.status != "PAID";
        else if (activeFilter == "completed") matchesStatus = order.status == "PAID";

        return matchesSearch && matchesDirection && matchesStatus;
    });

    // Statistics
    const stats = {
        total: myOrders.length,
        outgoing: myOrders.filter(o => o.pherAccountId == currentUserAccountId).length,
        incoming: myOrders.filter(o => o.gherAccountId == currentUserAccountId).length,
        completed: myOrders.filter(o => o.status == "PAID").length,
        totalVolume: myOrders.reduce((sum, o) => sum + o.donate, 0)
    };

    const OrderCard = ({ order }) => {
        const isOutgoing = order.pherAccountId == currentUserAccountId;
        const counterparty = isOutgoing ?
            { name: `${order?.gherFirstname} ${order?.gherSurname}`, username: order?.gherUsername, phone: order?.gherPhone } :
            { name: `${order?.pherFirstname} ${order?.pherSurname}`, username: order?.pherUsername, phone: order?.pherPhone };

        const statusInfo = getStatusInfo(order.status);
        const StatusIcon = statusInfo.icon;
        const blockingtimer = Number(order?.timestamp) + (60 * 60 * Number(order?.paywindow));

        return (
            <div className="group relative">
                {/* Subtle glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-600/20 via-yellow-500/10 to-amber-600/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden">
                    {/* Ambient light effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                    {/* Header */}
                    <div className="relative p-6 border-b border-gray-700/50">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-4">
                                <div className={`relative p-3 rounded-xl ${isOutgoing ? 'bg-gradient-to-br from-red-900/40 to-red-800/40 border border-red-700/30' : 'bg-gradient-to-br from-emerald-900/40 to-emerald-800/40 border border-emerald-700/30'}`}>
                                    {isOutgoing ? (
                                        <ArrowUpRight className="w-6 h-6 text-red-300" />
                                    ) : (
                                        <ArrowDownLeft className="w-6 h-6 text-emerald-300" />
                                    )}
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
                                </div>
                                <div>
                                    <h6 className="ext-lg font-bold text-white tracking-wide">{order.transid}</h6>
                                    <p className="text-gray-400 text-sm font-medium">
                                        {isOutgoing ? '📤 Make Payment Below' : '📥 Getting Paid'}
                                    </p>
                                </div>
                            </div>

                        </div>

                        <div className="flex items-center justify-between mb-6">
                            <div className={`px-4 py-2 rounded-xl border backdrop-blur-sm ${statusInfo.color} ${statusInfo.glow} shadow-lg`}>
                                <div className="flex items-center space-x-2">
                                    <StatusIcon className="w-4 h-4" />
                                    <span className="text-sm font-semibold tracking-wide">{statusInfo.label}</span>
                                </div>
                            </div>
                        </div>


                        {/* Amount Display */}
                        <div className="text-center py-6 bg-gradient-to-r from-gray-800/60 via-gray-700/60 to-gray-800/60 rounded-xl border border-gray-600/30 backdrop-blur-sm">
                            <p className="text-4xl font-black bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                                {money(order.donate, order.motherType)}
                            </p>
                            <p className="text-gray-300 text-sm mt-2 font-medium">
                                {/* {order.paywindow}h window */}
                                <Countdown
                                    date={new Date(Number(blockingtimer) * 1000)}
                                    renderer={({ hours, minutes, seconds, completed }) => {
                                        if (completed) {
                                            return <span>Time is up</span>
                                        } else {
                                            return <span>{hours}Hr : {minutes}Min : {seconds}Sec</span>;
                                        }
                                    }}
                                />

                            </p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="relative p-6 space-y-6">
                        {/* Counterparty Section */}
                        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-gray-800/40 via-gray-700/40 to-gray-800/40 rounded-xl border border-gray-600/30 backdrop-blur-sm">
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                                        <span className="text-gray-900 font-bold text-lg">
                                            {counterparty.username.split(' ').map(n => n[0]).join('')}
                                        </span>
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-gray-900 flex items-center justify-center">
                                        <Shield className="w-2 h-2 text-white" />
                                    </div>
                                </div>
                                <div>
                                    <p className="font-bold text-white text-lg">{counterparty?.name ?? counterparty?.username}</p>
                                    <p className="text-gray-300 text-sm flex items-center space-x-2">
                                        <span>@{counterparty.username}</span>
                                        <div className="flex items-center space-x-1">
                                            <Star className="w-3 h-3 text-amber-400 fill-current" />
                                        </div>
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">
                                    {isOutgoing ? 'Recipient' : 'Sender'}
                                </p>
                                <p className="text-sm font-semibold text-gray-200 mt-1">{counterparty.phone}</p>
                            </div>
                        </div>

                        {/* Payment Details Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-3 p-4 bg-gradient-to-br from-gray-800/40 to-gray-700/40 rounded-xl border border-gray-600/30">
                                <div className="flex items-center space-x-3">
                                    {order.autoConfirm ? (
                                        <div className="p-2 bg-blue-900/40 rounded-lg border border-blue-700/30">
                                            <Wallet className="w-5 h-5 text-blue-300" />
                                        </div>
                                    ) : (
                                        <div className="p-2 bg-purple-900/40 rounded-lg border border-purple-700/30">
                                            <Building2 className="w-5 h-5 text-purple-300" />
                                        </div>
                                    )}
                                    <span className="text-sm font-bold text-gray-200">
                                        {order.autoConfirm ? 'Cryptocurrency' : 'Bank Transfer'}
                                    </span>
                                </div>

                                {!order.autoConfirm && order.gherBank && (
                                    <div className="text-xs text-gray-300 space-y-1 pl-2 border-l-2 border-amber-500/30">
                                        <p className="font-semibold">{order.gherBank}</p>
                                        <p className="text-gray-400">{order.gherAccountname}</p>
                                        <p className="font-mono text-amber-300">{order.gherAccountnumber}</p>
                                    </div>
                                )}

                                {order.autoConfirm && order[`wallet${order.motherType}`] && (
                                    <div className="text-xs text-amber-300 pl-2 border-l-2 border-amber-500/30">
                                        <p className="font-mono break-all">{order[`wallet${order.motherType}`]}</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3 p-4 bg-gradient-to-br from-gray-800/40 to-gray-700/40 rounded-xl border border-gray-600/30">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-emerald-900/40 rounded-lg border border-emerald-700/30">
                                        <Activity className="w-5 h-5 text-emerald-300" />
                                    </div>
                                    <span className="text-sm font-bold text-gray-200">Transaction Info</span>
                                </div>
                                <div className="text-xs text-gray-300 space-y-2 pl-2 border-l-2 border-emerald-500/30">
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Peer Time:</span>
                                        <span className="font-semibold text-emerald-300">{formatDate(Number(order.timestamp) * 1000)} {formatTime(Number(order.timestamp) * 1000)}</span>
                                    </div>
                                    {order.reported == 1 && <div className="flex justify-between">
                                        <span className="text-gray-400">Reported:</span>
                                        <span className="font-semibold text-emerald-300">{order.reported == 1 ? 'Yes' : 'No'}</span>
                                    </div>}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between pt-6 border-t border-gray-700/50">
                            <div className="flex space-x-3">
                                {/* Upload Proof Button */}
                                {isOutgoing && ['NO', 'YES']?.includes(order.status) && (
                                    <>
                                        <button
                                            onClick={() => handleFileUpload(order.id)}
                                            disabled={loadingActions[`upload-proof-${order.id}`]}
                                            className="group flex items-center space-x-2 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-gray-900 px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 disabled:opacity-50 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transform hover:scale-105"
                                        >
                                            {loadingActions[`upload-proof-${order.id}`] ? (
                                                <RefreshCw className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Upload className="w-4 h-4" />
                                            )}
                                            <span>Upload Proof</span>
                                        </button>
                                        <input
                                            type="file"
                                            id={`file-${order?.id}`}
                                            className="hidden"
                                            onChange={(e) => handleFileChange(e, order?.id)}
                                            accept="image/*"
                                        />
                                    </>
                                )}

                                {/* Confirm Payment Button */}
                                {!isOutgoing && (
                                    <button
                                        onClick={() => handleMarkAsPaid(order.id)}
                                        disabled={loadingActions[`confirm-payment-${order.id}`]}
                                        className="group flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 disabled:opacity-50 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transform hover:scale-105"
                                    >
                                        {loadingActions[`confirm-payment-${order.id}`] ? (
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <CheckCircle className="w-4 h-4" />
                                        )}
                                        <span>Confirm Payment</span>
                                    </button>
                                )}

                                {/* View Proof Button */}
                                {order?.image != 0 && (
                                    <button
                                        onClick={() => {
                                            setSelectedProof(order);
                                            setShowProofModal(true);
                                        }}
                                        className="group flex items-center space-x-2 border-2 border-amber-500/50 hover:border-amber-400 text-amber-300 hover:text-amber-200 px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 hover:bg-amber-500/10 backdrop-blur-sm transform hover:scale-105"
                                    >
                                        <Eye className="w-4 h-4" />
                                        <span>View Proof</span>
                                    </button>
                                )}
                            </div>

                            <button className="p-3 text-gray-400 hover:text-amber-300 hover:bg-gray-700/50 rounded-xl transition-all duration-300">
                                <MoreVertical className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (<WrapUpP2P allowAutoPadding={true}>
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
            {/* Header */}
            <div className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-700/50 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search transactions..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-12 pr-4 py-3 bg-gray-800/60 border border-gray-600/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 text-white placeholder-gray-400 w-80 backdrop-blur-sm transition-all duration-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">

                {/* Filters */}
                <div className="bg-gray-800/40 backdrop-blur-sm p-6 rounded-2xl border border-gray-700/50 mb-8 shadow-xl">
                    <div className="flex flex-wrap items-center gap-6">
                        {/* Direction Filter */}
                        <div className="flex items-center space-x-3">
                            {/* <span className="text-sm font-bold text-gray-300">Direction:</span> */}
                            <div className="flex rounded-xl border border-gray-600/50 overflow-hidden bg-gray-800/50">
                                {[
                                    { id: 'all', label: 'All' },
                                    { id: 'outgoing', label: 'Outgoing' },
                                    { id: 'incoming', label: 'Incoming' }
                                ].map(option => (
                                    <button
                                        key={option.id}
                                        onClick={() => setDirectionFilter(option.id)}
                                        className={`px-6 py-2 text-sm font-semibold transition-all duration-300 ${directionFilter == option.id
                                            ? 'bg-gradient-to-r from-amber-600 to-yellow-500 text-gray-900 shadow-lg'
                                            : 'bg-transparent text-gray-300 hover:text-white hover:bg-gray-700/50'
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Status Filter */}
                        {1 > 2 && <div className="flex items-center space-x-3">
                            {/* <span className="text-sm font-bold text-gray-300">Status:</span> */}
                            <div className="flex rounded-xl border border-gray-600/50 overflow-hidden bg-gray-800/50">
                                {[
                                    { id: 'all', label: 'All' },
                                    { id: 'pending', label: 'Pending' },
                                    { id: 'completed', label: 'Completed' }
                                ].map(option => (
                                    <button
                                        key={option.id}
                                        onClick={() => setActiveFilter(option.id)}
                                        className={`px-6 py-2 text-sm font-semibold transition-all duration-300 ${activeFilter == option.id
                                            ? 'bg-gradient-to-r from-amber-600 to-yellow-500 text-gray-900 shadow-lg'
                                            : 'bg-transparent text-gray-300 hover:text-white hover:bg-gray-700/50'
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>}

                        <div className="ml-auto text-sm text-gray-400 font-medium">
                            Showing <span className="text-amber-300 font-bold">{filteredOrders.length}</span> of <span className="text-white font-bold">{myOrders.length}</span> orders
                        </div>
                    </div>
                </div>

                {/* Orders Grid */}
                {filteredOrders.length > 0 ? (
                    <div className="grid gap-8">
                        {filteredOrders.map(order => (
                            <OrderCard key={order.id} order={order} />
                        ))}
                    </div>
                ) : (
                    <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-16 text-center shadow-xl">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-600/20 to-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <Activity className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">No orders found</h3>
                        <p className="text-gray-400 max-w-md mx-auto">
                            {searchQuery || activeFilter != 'all' || directionFilter != 'all'
                                ? 'Try adjusting your filters or search query to find matching transactions.'
                                : 'Your P2P trading orders will appear here once you activate bidding.'}
                        </p>
                    </div>
                )}
            </div>
            {/* Proof Modal */}
            {showProofModal && (
                <div style={{ zIndex: 99999 }} className="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center p-4 z-50">
                    <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-gray-700/50 shadow-2xl">
                        <div className="flex items-center justify-between p-6 border-b border-gray-700/50 bg-gradient-to-r from-gray-800/50 to-gray-700/50">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <Eye className="w-5 h-5 text-gray-900" />
                                </div>
                                <h3 className="text-xl font-bold text-white">Payment Proof</h3>
                            </div>
                            <button
                                onClick={() => setShowProofModal(false)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-xl transition-all duration-300"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-8 overflow-y-auto max-h-[calc(80vh-120px)]">
                            <div className="bg-gradient-to-br from-gray-800/60 to-gray-700/60 rounded-2xl p-8 border border-gray-600/30 backdrop-blur-sm">
                                {/* Image Display */}
                                <div className="mb-6">
                                    <div className="relative bg-gray-800 rounded-xl overflow-hidden border border-gray-600/50">
                                        <img
                                            src={`${guessAdminRoute()}/assets/uploads/p2p/${selectedProof?.image}`}
                                            alt="Payment Proof"
                                            className="w-full h-auto max-h-96 object-contain"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    </div>
                                </div>


                                {/* Action Buttons */}
                                {selectedProof.gherAccountId == currentUserAccountId && (
                                    <div className="flex justify-center space-x-4 mt-8">
                                        <button
                                            onClick={() => handleMarkAsPaid(selectedProof.id)}
                                            className="flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-emerald-500/25 transform hover:scale-105"
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            <span>Confirm Proof</span>
                                        </button>
                                        <button
                                            onClick={() => reportTransaction(selectedProof.id)}
                                            className="flex items-center space-x-2 border-2 border-red-500/50 hover:border-red-400 text-red-300 hover:text-red-200 px-6 py-3 rounded-xl font-bold transition-all duration-300 hover:bg-red-500/10 transform hover:scale-105"
                                        >
                                            <AlertTriangle className="w-4 h-4" />
                                            <span>Report Issue</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {Object.values(loadingActions).some(Boolean) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center">
                    <div className="bg-gray-900 rounded-2xl p-8 border border-gray-700/50 text-center shadow-2xl">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/25">
                            <RefreshCw className="w-8 h-8 text-gray-900 animate-spin" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Processing Transaction</h3>
                        <p className="text-gray-400">Please wait while we process your request...</p>
                    </div>
                </div>
            )}

        </div>
    </WrapUpP2P>
    );
};

export default P2POrderDisplay;